import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { range } from 'lodash';

import QueryParamLink from 'components/atoms/QueryParamLink';

import styles from './index.module.scss';
import { getPageAnchorRelationToCurrentPage } from 'utils/url';

interface Props {
  currentPage: number;
  itemsPerPage: number;
  maxPagesToDisplay?: number;
  pageQueryParamName?: string;
  testId?: string;
  totalItems: number;
}

type PaginationItem = {
  currentPage: number;
  pageNumber: number;
  pageQueryParamName?: string;
  testId?: string;
};

const Pagination: FC<Props> = ({
  currentPage,
  itemsPerPage,
  maxPagesToDisplay = 10,
  pageQueryParamName = 'page',
  totalItems,
  testId = '',
}) => {
  const { t } = useTranslation();

  const numberOfPages = Math.ceil(totalItems / itemsPerPage);
  const displayedPageNumbers = useMemo(() => {
    if (numberOfPages <= maxPagesToDisplay) return range(1, numberOfPages + 1);

    if (currentPage <= maxPagesToDisplay / 2) return range(1, maxPagesToDisplay + 1);

    if (currentPage < numberOfPages - maxPagesToDisplay / 2)
      return range(
        currentPage - Math.floor(maxPagesToDisplay / 2),
        currentPage + Math.ceil(maxPagesToDisplay / 2)
      );

    return range(numberOfPages - maxPagesToDisplay + 1, numberOfPages + 1);
  }, [currentPage, maxPagesToDisplay, numberOfPages]);

  if (numberOfPages === 0) {
    return null;
  }

  return (
    <ul className={styles.list}>
      {currentPage > 1 && (
        <li className={classNames(styles.item, styles.first)}>
          <QueryParamLink
            params={{
              [pageQueryParamName]: currentPage - 1 > 1 ? (currentPage - 1).toString() : '',
            }}
          >
            <a rel="prev" className={styles.button} data-test-id={`${testId}-page-nav-prev`}>
              {t('previous')}
            </a>
          </QueryParamLink>
        </li>
      )}
      {displayedPageNumbers.map((page) => (
        <PaginationItem
          key={page}
          currentPage={currentPage}
          pageNumber={page}
          pageQueryParamName={pageQueryParamName}
          testId={testId}
        />
      ))}
      {currentPage < numberOfPages && (
        <li className={classNames(styles.item, styles.last)}>
          <QueryParamLink params={{ [pageQueryParamName]: (currentPage + 1).toString() }}>
            <a rel="next" className={styles.button} data-test-id={`${testId}-page-nav-next`}>
              {t('next')}
            </a>
          </QueryParamLink>
        </li>
      )}
    </ul>
  );
};

const PaginationItem: FC<PaginationItem> = ({
  currentPage,
  pageNumber,
  pageQueryParamName = 'page',
  testId = '',
}) => {
  const { t } = useTranslation();
  const rel = useMemo(() => getPageAnchorRelationToCurrentPage(currentPage, pageNumber), [
    currentPage,
    pageNumber,
  ]);

  return (
    <li
      className={classNames(styles.item, styles.number)}
      aria-label={t('pageNumber', { number: pageNumber })}
    >
      {currentPage === pageNumber ? (
        <span data-test-id={`${testId}-current-page-link`}>{pageNumber}</span>
      ) : (
        <QueryParamLink
          params={{ [pageQueryParamName]: pageNumber === 1 ? '' : pageNumber.toString() }}
        >
          <a rel={rel}>{pageNumber}</a>
        </QueryParamLink>
      )}
    </li>
  );
};

export type { Props as PaginationProps };
export default Pagination;
